import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Toaster } from 'sonner';

import Home from './pages/Home';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Welcome from './pages/Welcome';
import Assess from './pages/Assess';
import LifeProfile from './pages/LifeProfile';
import MyLife from './pages/MyLife';
import Timeline from './pages/Timeline';
import Community from './pages/Community';
import AskSummaros from './pages/AskSummaros';
import Profile from './pages/Profile';
import HowItWorks from './pages/HowItWorks';

import AppShell from './components/AppShell';
import { installAuthInterceptor } from './lib/auth';
import './App.css';

// Install once at module load — idempotent.
installAuthInterceptor();

const NotFound = () => (
  <AppShell>
    <section className="max-w-2xl mx-auto px-6 py-32 text-center">
      <p className="eyebrow mb-5">404</p>
      <h1 className="display-serif text-4xl sm:text-5xl text-forest mb-4">
        Not here.
      </h1>
      <p className="text-ink-muted mb-8">
        This page doesn't exist. Or maybe it hasn't been built yet.
      </p>
      <Link to="/" className="btn-primary">Back home</Link>
    </section>
  </AppShell>
);

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      if (!location.hash) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, location.search, location.hash]);

  // Emergent auth returns session_id in the URL fragment. Handle it
  // regardless of the route the user landed on.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/how" element={<HowItWorks />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/assess" element={<Assess />} />
      <Route path="/life-profile" element={<LifeProfile />} />
      <Route path="/my-life" element={<MyLife />} />
      <Route path="/timeline" element={<Timeline />} />
      <Route path="/community" element={<Community />} />
      <Route path="/ask" element={<AskSummaros />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-cream">
      <Toaster
        position="top-center"
        theme="light"
        toastOptions={{
          style: {
            background: '#F4F0E7',
            color: '#28382F',
            border: '1px solid #C8BFA6',
            borderRadius: '9999px',
          },
        }}
      />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
